import React, { useCallback, useState } from "react";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import QmsPublishConfirmForm, { PUBLISH_FORM_ID } from "./QmsPublishConfirmForm";
import QmsPublishStaffDraftPanel from "./QmsPublishStaffDraftPanel";
import QmsMyLearningAssignmentPanel, {
  MyLearningSaveBar,
} from "./QmsMyLearningAssignmentPanel";
import {
  countAssignedStaffRows,
  promptDiscardMyLearningChanges,
} from "./qmsPublishStaffUtils";
const STEPS = [
  {
    id: "myLearning",
    shortLabel: "My Learning",
    icon: "fas fa-graduation-cap",
  },
  {
    id: "library",
    shortLabel: "Library",
    icon: "fas fa-book",
  },
  {
    id: "publish",
    shortLabel: "Publish",
    icon: "fas fa-globe",
  },
];

function QmsPublishStepperPanel({
  myLearningStaff = [],
  libraryStaff = [],
  workspaceResponse,
  row,
  loading = false,
  saving = false,
  canPublish = true,
  onMyLearningSave,
  onLibrarySave,
  onPublish,
  onMyLearningDirtyChange,
  myLearningStaffTrainingRoles = [],
}) {
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [activeStep, setActiveStep] = useState(0);
  const [myLearningDirty, setMyLearningDirty] = useState(false);
  const [myLearningSaveBar, setMyLearningSaveBar] = useState(null);
  const step = STEPS[activeStep];
  const myLearningCount = countAssignedStaffRows(myLearningStaff);
  const libraryCount = countAssignedStaffRows(libraryStaff);
  const isLastStep = activeStep === STEPS.length - 1;

  const handleMyLearningDirtyChange = useCallback(
    (dirty) => {
      setMyLearningDirty(dirty);
      onMyLearningDirtyChange?.(dirty);
    },
    [onMyLearningDirtyChange],
  );

  const goToStep = useCallback(
    (nextStep) => {
      if (nextStep === activeStep) return;

      if (step.id === "myLearning" && myLearningDirty) {
        promptDiscardMyLearningChanges(showAlert, () => {
          setMyLearningDirty(false);
          setActiveStep(nextStep);
        });
        return;
      }

      setActiveStep(nextStep);
    },
    [activeStep, myLearningDirty, showAlert, step.id],
  );

  const goNext = () => {
    if (activeStep < STEPS.length - 1) goToStep(activeStep + 1);
  };

  const goBack = () => {
    if (activeStep > 0) goToStep(activeStep - 1);
  };

  return (
    <>
      <div
        className={`qms-doc-workspace__panel qms-publish-stepper${
          isLastStep ? " is-publish-step" : ""
        }`}
      >
      <nav className="qms-publish-stepper__track" aria-label="Publish steps">
        {STEPS.map((item, index) => {
          const isActive = index === activeStep;
          const isComplete = index < activeStep;
          const connectorComplete = index < activeStep;

          return (
            <React.Fragment key={item.id}>
              <button
                type="button"
                className={`qms-publish-stepper__node${
                  isActive ? " is-active" : ""
                }${isComplete ? " is-complete" : ""}`}
                onClick={() => goToStep(index)}              >
                <span className="qms-publish-stepper__node-circle" aria-hidden="true">
                  {isComplete ? (
                    <i className="fas fa-check" aria-hidden="true" />
                  ) : (
                    <i className={item.icon} aria-hidden="true" />
                  )}
                </span>
                <span className="qms-publish-stepper__node-label">{item.shortLabel}</span>
              </button>

              {index < STEPS.length - 1 ? (
                <div
                  className={`qms-publish-stepper__connector${
                    connectorComplete ? " is-complete" : ""
                  }${isActive ? " is-current" : ""}`}
                  aria-hidden="true"
                >
                  <span className="qms-publish-stepper__connector-line" />
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </nav>

      <div
        className={`qms-publish-stepper__body${
          step.id !== "publish" ? " is-staff" : " is-publish"
        }`}
      >
        {step.id === "myLearning" ? (
          <QmsMyLearningAssignmentPanel
            staff={myLearningStaff}
            staffTrainingAdditionalRoles={myLearningStaffTrainingRoles}
            documentId={row?.DocumentId}
            loading={loading}
            saving={saving}
            onSave={onMyLearningSave}
            onDirtyChange={handleMyLearningDirtyChange}
            savePlacement="footer"
            onSaveBarStateChange={setMyLearningSaveBar}
          />
        ) : null}

        {step.id === "library" ? (
          <QmsPublishStaffDraftPanel
            staff={libraryStaff}
            documentId={row?.DocumentId}
            loading={loading}
            saving={saving}
            onSave={onLibrarySave}
            className="qms-publish-ml-staff qms-publish-lib-staff"
          />
        ) : null}

        {step.id === "publish" ? (
          canPublish ? (
            <QmsPublishConfirmForm
              row={row}
              workspaceResponse={workspaceResponse}
              saving={saving}
              myLearningCount={myLearningCount}
              libraryCount={libraryCount}
              onSubmit={onPublish}
            />
          ) : (
            <p className="text-muted mb-0 small">
              This document is not ready to publish. It must be Approval Completed and not checked
              out.
            </p>
          )
        ) : null}
      </div>

      <div className="qms-publish-stepper__footer">
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={activeStep === 0 || saving}
          onClick={goBack}
        >
          <i className="fas fa-arrow-left me-1" aria-hidden="true" />
          Back
        </button>

        {step.id === "myLearning" && myLearningSaveBar ? (
          <div className="qms-publish-stepper__footer-save">
            <MyLearningSaveBar
              assignedCount={myLearningSaveBar.assignedCount}
              isDirty={myLearningSaveBar.isDirty}
              isSaving={myLearningSaveBar.isSaving}
              onSave={myLearningSaveBar.onSave}
              className="qms-publish-ml-staff__save-bar--footer"
            />
          </div>
        ) : (
          <span className="qms-publish-stepper__footer-spacer" aria-hidden="true" />
        )}

        {!isLastStep ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={goNext}
          >
            Next
            <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
          </button>
        ) : canPublish ? (
          <button
            type="submit"
            form={PUBLISH_FORM_ID}
            className="btn btn-success"
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                Publishing…
              </>
            ) : (
              <>
                <i className="fas fa-globe me-1" aria-hidden="true" />
                Publish document
              </>
            )}
          </button>
        ) : null}
      </div>
      </div>

      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}

export default QmsPublishStepperPanel;
